import api from "./api";
import { AdminRegisterCredentials, LoginCredentials, LoginResponse, User } from "@/types/auth";
import { clearCsrfToken, ensureCsrfToken } from "@/utils/csrf";

export const authService = {
  ensureCsrf: async (): Promise<void> => {
    await ensureCsrfToken();
  },

  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Always re-bootstrap CSRF before login so we don't reuse an in-memory
    // token after the csrftoken cookie was dropped (e.g. SameSite mismatch).
    clearCsrfToken();
    await ensureCsrfToken();
    try {
      const response = await api.post<LoginResponse>("/auth/login/", credentials);
      return response.data;
    } catch (err) {
      clearCsrfToken();
      throw err;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await ensureCsrfToken();
      await api.post("/auth/logout/");
    } finally {
      clearCsrfToken();
    }
  },

  refreshSession: async (): Promise<void> => {
    await ensureCsrfToken();
    await api.post("/auth/refresh/");
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>("/accounts/me/");
    return response.data;
  },

  changePassword: async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> => {
    await api.post("/accounts/change-password/", {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  },

  registerAdmin: async (data: AdminRegisterCredentials): Promise<void> => {
    await ensureCsrfToken();
    await api.post("/auth/register/admin/", data);
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post("/auth/password-reset/request/", { email });
  },

  verifyResetCode: async (email: string, code: string): Promise<void> => {
    await api.post("/auth/password-reset/verify/", { email, code });
  },

  confirmPasswordReset: async (
    email: string,
    code: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> => {
    await api.post("/auth/password-reset/confirm/", {
      email,
      code,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  },
};
